import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
    Button, Card, CardContent, FormControl, Grid,
    TextField, Typography, Autocomplete, Alert, Snackbar,
} from "@mui/material";
import { DateTimePicker } from "@mui/lab";
import { Formik } from "formik";
import * as Yup from "yup";
import axios from "../../CustomAxios";
import Loading from "../components/Loading";
import { connect } from "react-redux";

const PlatePriceScheduleCreate = ({ auths }) => {
    const authData = JSON.parse(localStorage.getItem("AuthData"));
    const navigate = useNavigate();
    const [companies, setCompanies] = useState([]);
    const [plates, setPlates] = useState([]);
    const [loader, setLoader] = useState(false);
    const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });

    useEffect(() => {
        // Fetch all customer companies
        const fetchCompanies = async () => {
            const config = {
                headers: {
                    Authorization: "Bearer " + authData.access_token,
                },
            };
            try {
                const { data } = await axios.get(`/api/v1/${authData.type}/customer_company?limit=500`, config);
                if (data.success) setCompanies(data.data.data.filter(c => !c.is_deleted));
            } catch (e) { console.error(e); }
        };
        // Fetch all plates
        const fetchPlates = async () => {
            const config = {
                headers: {
                    Authorization: "Bearer " + authData.access_token,
                },
            };
            try {
                const { data } = await axios.get(`/api/v1/${authData.type}/plate_inventory?limit=500`, config);
                if (data.success) setPlates(data.data.data.filter(p => !p.is_deleted));
            } catch (e) { console.error(e); }
        };
        fetchCompanies();
        fetchPlates();
    }, []);

    const handleSubmit = async (values, { setSubmitting, resetForm }) => {
        try {
            setLoader(true);
            const payload = {
                companies: values.companies.map(c => c._id),
                plate: values.plate._id,
                baking_cost: values.baking_cost,
                print_cost: values.print_cost,
                selling_cost: values.selling_cost,
                gripper: values.gripper,
                effective_date: values.effective_date,
            };
            const config = { headers: { Authorization: `Bearer ${authData.access_token}` } };
            await axios.post(`/api/v1/${authData.type}/plate_price_schedules`, payload, config);
            setSnack({ open: true, message: "Schedules created successfully", severity: "success" });
            resetForm();
            setLoader(false);
            navigate("/dashboard/plate-price-schedules");
        } catch (error) {
            setLoader(false);
            setSnack({ open: true, message: error.response?.data?.error || "Error", severity: "error" });
        }
        setSubmitting(false);
    };

    return (
        <>
            <Helmet title="Create Effective Cost" />
            <Grid container spacing={6}>
                <Grid item xs={12}>
                    <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }} color="primary">
                        Create Effective Cost
                    </Typography>
                </Grid>
            </Grid>

            {!loader ? (
                <Formik
                    initialValues={{
                        companies: [],
                        plate: null,
                        baking_cost: "",
                        print_cost: "",
                        selling_cost: "",
                        gripper: "",
                        effective_date: new Date(),
                    }}
                    validationSchema={Yup.object({
                        companies: Yup.array().min(1, "Select at least one company"),
                        plate: Yup.object().required("Select a plate"),
                        baking_cost: Yup.number().required("Required"),
                        print_cost: Yup.number().required("Required"),
                        selling_cost: Yup.number().required("Required"),
                        gripper: Yup.number().min(0).max(99.99).required("Required"),
                        effective_date: Yup.date().required("Required"),
                    })}
                    onSubmit={handleSubmit}
                >
                    {({ values, errors, touched, handleChange, handleSubmit, setFieldValue, isSubmitting }) => {
                        // Filter plates based on selected companies
                        const availablePlates = useMemo(() => {
                            if (values.companies.length === 0) return [];
                            const associatedPlateIds = new Set();
                            values.companies.forEach((company) => {
                                (company.price || []).forEach((item) => {
                                    if (item.plate_id && !item.deactivated) {
                                        associatedPlateIds.add(item.plate_id.toString());
                                    }
                                });
                            });
                            return plates.filter((plate) => associatedPlateIds.has(plate._id.toString()));
                        }, [plates, values.companies]);

                        // Clear plate if it's no longer available after company change
                        useEffect(() => {
                            if (values.plate && !availablePlates.some((p) => p._id === values.plate._id)) {
                                setFieldValue("plate", null);
                            }
                        }, [availablePlates, values.plate, setFieldValue]);

                        return (
                            <form noValidate onSubmit={handleSubmit}>
                                <Card>
                                    <CardContent>
                                        <Grid container spacing={3}>
                                            <Grid item xs={12} md={6}>
                                                <Autocomplete
                                                    size="small"
                                                    multiple
                                                    options={companies}
                                                    value={values.companies}
                                                    onChange={(e, newValue) => setFieldValue("companies", newValue)}
                                                    getOptionLabel={(option) => option.name}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            label="Select Customer Companies"
                                                            error={touched.companies && !!errors.companies}
                                                            helperText={touched.companies && errors.companies}
                                                        />
                                                    )}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <Autocomplete
                                                    size="small"
                                                    options={availablePlates}
                                                    value={values.plate}
                                                    onChange={(e, newValue) => setFieldValue("plate", newValue)}
                                                    getOptionLabel={(option) => `${option.height}x${option.width}x${option.thickness} - ${option.company}`}
                                                    disabled={values.companies.length === 0}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            label="Select Plate"
                                                            error={touched.plate && !!errors.plate}
                                                            helperText={
                                                                touched.plate && errors.plate
                                                                    ? errors.plate
                                                                    : values.companies.length === 0
                                                                        ? "Select at least one customer company first"
                                                                        : ""
                                                            }
                                                        />
                                                    )}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={3}>
                                                <TextField
                                                    size="small"
                                                    fullWidth
                                                    name="baking_cost"
                                                    label="Baking Cost"
                                                    type="number"
                                                    value={values.baking_cost}
                                                    onChange={handleChange}
                                                    error={touched.baking_cost && !!errors.baking_cost}
                                                    helperText={touched.baking_cost && errors.baking_cost}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={3}>
                                                <TextField
                                                    size="small"
                                                    fullWidth
                                                    name="print_cost"
                                                    label="Print Cost"
                                                    type="number"
                                                    value={values.print_cost}
                                                    onChange={handleChange}
                                                    error={touched.print_cost && !!errors.print_cost}
                                                    helperText={touched.print_cost && errors.print_cost}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={3}>
                                                <TextField
                                                    size="small"
                                                    fullWidth
                                                    name="selling_cost"
                                                    label="Selling Cost"
                                                    type="number"
                                                    value={values.selling_cost}
                                                    onChange={handleChange}
                                                    error={touched.selling_cost && !!errors.selling_cost}
                                                    helperText={touched.selling_cost && errors.selling_cost}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={3}>
                                                <TextField
                                                    size="small"
                                                    fullWidth
                                                    name="gripper"
                                                    label="Gripper"
                                                    type="number"
                                                    inputProps={{ step: "0.01", min: 0, max: 99.99 }}
                                                    value={values.gripper}
                                                    onChange={handleChange}
                                                    error={touched.gripper && !!errors.gripper}
                                                    helperText={touched.gripper && errors.gripper}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={3}>
                                                <FormControl fullWidth>
                                                    <DateTimePicker
                                                        size="small"
                                                        label="Effective Date"
                                                        value={values.effective_date}
                                                        onChange={(newValue) => setFieldValue("effective_date", newValue)}
                                                        renderInput={(params) => (
                                                            <TextField
                                                                {...params}
                                                                error={touched.effective_date && !!errors.effective_date}
                                                                helperText={touched.effective_date && errors.effective_date}
                                                            />
                                                        )}
                                                    />
                                                </FormControl>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Button type="submit" variant="contained" color="primary" disabled={isSubmitting}>
                                                    Create Schedules
                                                </Button>
                                            </Grid>
                                        </Grid>
                                    </CardContent>
                                </Card>
                            </form>
                        );
                    }}
                </Formik>
            ) : (
                <Loading />
            )}

            <Snackbar open={snack.open} autoHideDuration={6000} onClose={() => setSnack({ ...snack, open: false })}>
                <Alert severity={snack.severity}>{snack.message}</Alert>
            </Snackbar>
        </>
    );
};

const mapStateToProps = (state) => ({ auths: state.auths });
export default connect(mapStateToProps)(PlatePriceScheduleCreate);